import { Dict } from 'mixpanel-browser';
import { useCallback, useContext } from 'react';

import { mixpanelContext } from '@/contexts/mixpanelContext';
import { OnboardingNodeType } from '@/pages/members/pages/Onboarding/steps/types';
import { defaultOptionTypes } from '@/ui/components/Form/Select/types';
import { SelectOption } from '@/ui/members/components/form/SelectDialog/types';
import isRunningInReactNativeWebView from '@/utils/isRunningInReactNativeWebView';

import { ButtonPressedEventParams } from './types';
import { AnalyticsTrackingEvents } from './utils';

type ResolutionType = 'completed' | 'skipped' | 'bypassed';

type MixpanelTracker = {
  identify: (distinctId: string) => void;
  track: (eventName: string, properties?: Dict) => void;
  trackError: (errorMessage: string, screenName?: string, extraArgs?: Dict) => void;
  trackButtonPress: (params: ButtonPressedEventParams, extraArgs?: Dict) => void;
  trackRadioSelected: (
    id: string,
    value: defaultOptionTypes,
    screenName?: string,
    extraArgs?: Dict
  ) => void;
  trackCheckboxClicked: (id: string, value: boolean, screenName?: string, extraArgs?: Dict) => void;
  trackComboboxSelected: (
    id: string,
    option: SelectOption,
    screenName?: string,
    extraArgs?: Dict
  ) => void;
  trackOnboardingResolvedStep: (
    screenName: OnboardingNodeType,
    version: number,
    resolutionType: ResolutionType
  ) => void;
  registerSuperProperties: (properties: Dict) => void;
};

const useMixpanel = (): MixpanelTracker => {
  const _mixpanel = useContext(mixpanelContext);

  // super properties
  // there are more super properties being set when they become available in the onboarding flow
  _mixpanel?.register({
    sha: process.env.VITE_APP_GIT_SHA,
    webview: isRunningInReactNativeWebView(),
    location: 'web'
  });

  const track = useCallback(
    (eventName: string, properties?: Dict) => {
      _mixpanel?.track(eventName, properties);
    },
    [_mixpanel]
  );

  const trackError = useCallback(
    (errorMessage: string, screenName?: string, extraArgs?: Dict) => {
      const event = AnalyticsTrackingEvents.error(errorMessage, screenName);
      _mixpanel?.track(event, { screenName, ...extraArgs });
    },
    [_mixpanel]
  );

  const trackButtonPress = useCallback(
    ({ buttonTitle, buttonText, screenName }: ButtonPressedEventParams, extraArgs?: Dict) => {
      const event = AnalyticsTrackingEvents.buttonPressed(buttonTitle);
      _mixpanel?.track(event, {
        buttonText,
        screenName,
        ...extraArgs
      });
    },
    [_mixpanel]
  );

  const trackRadioSelected = useCallback(
    (id: string, value: defaultOptionTypes, screenName?: string, extraArgs?: Dict) => {
      const event = AnalyticsTrackingEvents.radioSelected(id, value);
      _mixpanel?.track(event, { screenName, ...extraArgs });
    },
    [_mixpanel]
  );

  const trackCheckboxClicked = useCallback(
    (id: string, value: boolean, screenName?: string, extraArgs?: Dict) => {
      const event = AnalyticsTrackingEvents.checkboxClicked(id, value);
      _mixpanel?.track(event, { screenName, ...extraArgs });
    },
    [_mixpanel]
  );

  const trackComboboxSelected = useCallback(
    (id: string, option: SelectOption, screenName?: string, extraArgs?: Dict) => {
      const event = AnalyticsTrackingEvents.comboboxSelected(id, option);
      _mixpanel?.track(event, { screenName, ...extraArgs });
    },
    [_mixpanel]
  );

  const trackOnboardingResolvedStep = useCallback(
    (screenName: OnboardingNodeType, version: number, resolutionType: ResolutionType) => {
      _mixpanel?.track('Step Resolved', {
        screenName: `onboarding:${screenName}`,
        version,
        resolutionType,
        feature: 'unified-onboarding'
      });

      if (resolutionType === 'completed') {
        _mixpanel?.track('Step Completed', {
          screenName: `onboarding:${screenName}`,
          version,
          feature: 'unified-onboarding'
        });
      }

      if (resolutionType === 'skipped') {
        _mixpanel?.track('Step Skipped', {
          screenName: `onboarding:${screenName}`,
          version,
          feature: 'unified-onboarding'
        });
      }

      if (resolutionType === 'bypassed') {
        _mixpanel?.track('Step Bypassed', {
          screenName: `onboarding:${screenName}`,
          version,
          feature: 'unified-onboarding'
        });
      }
    },
    [_mixpanel]
  );

  const registerSuperProperties = useCallback(
    (properties: Dict) => {
      _mixpanel?.register(properties);
    },
    [_mixpanel]
  );

  const identify = useCallback(
    (distinctId: string) => {
      _mixpanel?.identify(distinctId);
    },
    [_mixpanel]
  );

  return {
    identify,
    track,
    trackError,
    trackButtonPress,
    trackRadioSelected,
    trackCheckboxClicked,
    trackComboboxSelected,
    trackOnboardingResolvedStep,
    registerSuperProperties
  };
};

export default useMixpanel;
